// ServiceAnalysis.js - based on HybridAnalysis.js, but only shows scheduled, actual, and proposed (best OTR) lines
import React, { useState, useMemo } from 'react';
import { 
  Card, Typography, Alert, Space, Select, Row, Col, 
  Empty, Switch, Divider, Button, Tag
} from 'antd';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  ReferenceArea
} from 'recharts';

const { Option } = Select;
const { Text, Paragraph } = Typography;

// Custom Tooltip for better alignment and color
const tooltipLineColors = {
  scheduledTime: '#8884d8',
  proposedTime: '#52c41a', // OTR line
  normalTime: '#1890ff',
  empiricalTime: '#faad14',
  logNormalTime: '#d46b08',
  weibullTime: '#722ed1',
  robustTime: '#13c2c2',
  actualTime: '#999999',
};

const CustomTooltip = ({ active, label, payload }) => {
  if (!active || !payload || !payload.length) return null;
  // Filter out null/hidden values and actual times
  const filtered = payload.filter(
    item =>
      item.value !== null &&
      item.value !== undefined &&
      item.name !== 'Actual Time' &&
      !(typeof item.name === 'string' && item.name.startsWith('actualTime'))
  );
  return (
    <div style={{ background: 'white', border: '1px solid #eee', borderRadius: 8, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div style={{ fontWeight: 500, marginBottom: 8 }}>Stop: {label}</div>
      <table style={{ borderCollapse: 'collapse' }}>
        <tbody>
          {filtered.map((item, idx) => {
            // Format time as HH:MM
            let displayValue = item.value;
            if (typeof displayValue === 'number' && displayValue > 1000000) {
              const date = new Date(displayValue);
              const hours = date.getUTCHours().toString().padStart(2, '0');
              const minutes = date.getUTCMinutes().toString().padStart(2, '0');
              const seconds = date.getUTCSeconds().toString().padStart(2, '0');
              displayValue = `${hours}:${minutes}:${seconds}`;
            }
            // Use item.color if available, else fallback to our map using dataKey
            let color = item.stroke ||item.color || tooltipLineColors[item.dataKey] || '#333';
            return (
              <tr key={item.name || idx}>
                <td style={{ color, fontWeight: 500, paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{
                  item.dataKey === 'proposedTime' && item.payload && typeof item.payload.proposedOTR === 'number'
                    ? `Best OTR (${item.payload.proposedOTR.toFixed(1)}%)`
                    : (typeof item.name === 'function' ? item.name(item, idx) : item.name)
                }</td>
                <td style={{ color, fontWeight: 400, textAlign: 'right', minWidth: 40 }}>{displayValue}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Add this function before the ServiceAnalysis component
const CustomXAxisTick = ({ x, y, payload, isTimingPoint }) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill={isTimingPoint ? "#1890ff" : "#666"}
        transform="rotate(-45)"
        style={{ fontWeight: isTimingPoint ? 'bold' : 'normal' }}
      >
        {payload.value}
        {isTimingPoint && (
          <tspan dx={4} fill="#1890ff">●</tspan>
        )}
      </text>
    </g>
  );
};

export const ServiceAnalysis = ({ 
  reportData,
  proposedOTRWindowTimetables, // expects the same data shape as ProposedOTRWindowTimetables.js
  selectedRoute,
  selectedTrip,
  setSelectedRoute,
  setSelectedTrip,
  dateRange,
  initialShowStopTimes = true,
  initialShowScheduledTime = true,
  initialShowProposedTime = true,
  initialShowModelLines = true
}) => {
  const [showStopTimes, setShowStopTimes] = useState(initialShowStopTimes);
  const [showScheduledTime, setShowScheduledTime] = useState(initialShowScheduledTime);
  const [showProposedTime, setShowProposedTime] = useState(initialShowProposedTime);
  const [showModelLines, setShowModelLines] = useState(initialShowModelLines);

  // Get unique routes for filter dropdown
  const routes = [...new Set(reportData.parsedData.map(item => item.routeNo))].sort();

  // Process data for the selected trip
  const processData = useMemo(() => {
    if (!selectedRoute || !selectedTrip || selectedRoute === 'all' || selectedTrip === 'all') {
      return null;
    }

    // Parse date range for filtering
    let rangeStart = null, rangeEnd = null;
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      rangeStart = dateRange[0].format ? dateRange[0].format('YYYY-MM-DD') : String(dateRange[0]);
      rangeEnd = dateRange[1].format ? dateRange[1].format('YYYY-MM-DD') : String(dateRange[1]);
    }

    // Filter for selected route and trip
    let filtered = reportData.parsedData.filter(item =>
      item.routeNo === selectedRoute &&
      item.tripId === selectedTrip
    );

    // Filter by date range if available
    if (rangeStart && rangeEnd) {
      filtered = filtered.filter(item => {
        const tripDate = item.tripDate || item.date;
        return tripDate && tripDate >= rangeStart && tripDate <= rangeEnd;
      });
    }

    // Group by tripDate (each trip instance)
    const tripInstances = {};
    filtered.forEach(stop => {
      const tripDate = stop.tripDate || 'Unknown Date';
      if (!tripInstances[tripDate]) tripInstances[tripDate] = [];
      tripInstances[tripDate].push(stop);
    });

    // For each trip instance, sort stops by TIME_RECORD_SEQUENCE (or scheduledTime) and exclude stops with actualTime of 0
    const chartLines = Object.entries(tripInstances).map(([tripDate, stops]) => {
      // Exclude stops where actualTime parses to 0 (i.e., '00:00:00' or missing/invalid)
      const filteredStops = stops.filter(stop => {
        const [h, m, s] = (stop.actualTime?.split(' ')[1] || '00:00:00').split(':').map(Number);
        const actualMs = ((h || 0) * 60 * 60 + (m || 0) * 60 + (s || 0)) * 1000;
        return actualMs !== 0;
      });
      const sortedStops = [...filteredStops].sort((a, b) => {
        // Prefer TIME_RECORD_SEQUENCE, fallback to scheduledTime
        const seqA = parseInt(a.TIME_RECORD_SEQUENCE || a.stopSequence || '0', 10);
        const seqB = parseInt(b.TIME_RECORD_SEQUENCE || b.stopSequence || '0', 10);
        if (!isNaN(seqA) && !isNaN(seqB) && seqA !== seqB) return seqA - seqB;
        // Fallback: sort by scheduledTime
        const [dateA, timeA] = (a.scheduledTime || '').split(' ');
        const [dateB, timeB] = (b.scheduledTime || '').split(' ');
        return (timeA || '').localeCompare(timeB || '');
      });
      return {
        tripDate,
        stops: sortedStops
      };
    });

    // Build chart data: one row per stop (by stopId), columns for each trip instance's actual time
    // Get all unique stopIds in order of first appearance
    const allStopIds = [];
    chartLines.forEach(line => {
      line.stops.forEach(stop => {
        if (!allStopIds.includes(stop.stopId)) allStopIds.push(stop.stopId);
      });
    });

    // Build chartData: [{ stopName, stopId, scheduledTime, actual_<tripDate>: ms, ... }]
    const chartData = allStopIds.map(stopId => {
      // Use the first occurrence for stopName and scheduledTime
      let stopName = '';
      let scheduledTime = null;
      chartLines.forEach(line => {
        const found = line.stops.find(s => s.stopId === stopId);
        if (found && !stopName) stopName = found.stopName || `Stop ${stopId}`;
        if (found && scheduledTime === null) {
          const [h, m, s] = (found.scheduledTime?.split(' ')[1] || '00:00:00').split(':').map(Number);
          scheduledTime = ((h || 0) * 60 * 60 + (m || 0) * 60 + (s || 0)) * 1000;
        }
      });
      const row = { stopName, stopId, scheduledTime };
      chartLines.forEach(line => {
        const found = line.stops.find(s => s.stopId === stopId);
        if (found) {
          const [h, m, s] = (found.actualTime?.split(' ')[1] || '00:00:00').split(':').map(Number);
          row[`actual_${line.tripDate}`] = ((h || 0) * 60 * 60 + (m || 0) * 60 + (s || 0)) * 1000;
        } else {
          row[`actual_${line.tripDate}`] = null;
        }
      });
      return row;
    });

    // Also get proposed/model times for the first trip instance (if available)
    let proposedData = [];
    if (proposedOTRWindowTimetables && proposedOTRWindowTimetables.length > 0) {
      proposedData = proposedOTRWindowTimetables.filter(item =>
        item.routeNo === selectedRoute &&
        item.tripId === selectedTrip
      );
    }
    // Add proposed/model times to chartData if available
    chartData.forEach(row => {
      const proposed = proposedData.find(p => p.stopId === row.stopId);
      if (proposed && proposed.proposedTime) {
        const timePart = proposed.proposedTime.split(' ')[1] || proposed.proposedTime;
        const [h, m, s] = timePart.split(':').map(Number);
        row.proposedTime = ((h || 0) * 60 * 60 + (m || 0) * 60 + (s || 0)) * 1000;
      }
      if (proposed && proposed.modelResults) {
        row.normalTime = proposed.modelResults.normal?.recommendedTime;
        row.empiricalTime = proposed.modelResults.empirical?.recommendedTime;
        row.logNormalTime = proposed.modelResults.logNormal?.recommendedTime;
        row.weibullTime = proposed.modelResults.weibull?.recommendedTime;
        row.robustTime = proposed.modelResults.robust?.recommendedTime;
        row.proposedOTR = proposed.percentInWindow;
      }
    });

    // Add timing point information to chartData
    chartData.forEach(row => {
      const stop = reportData.parsedData.find(item => 
        item.stopId === row.stopId && 
        item.routeNo === selectedRoute
      );
      row.isTimingPoint = stop?.isTimingPoint || false;
    });

    // Return both chartData and tripDates for legend
    return { chartData, tripDates: chartLines.map(line => line.tripDate) };
  }, [reportData, proposedOTRWindowTimetables, selectedRoute, selectedTrip, dateRange]);

  const chartData = processData ? processData.chartData : [];
  const tripDates = processData ? processData.tripDates : [];

  // Move zoom state initialization after chartData is defined
  const [refAreaLeft, setRefAreaLeft] = useState('');
  const [refAreaRight, setRefAreaRight] = useState('');
  const [left, setLeft] = useState(0);
  const [right, setRight] = useState(chartData.length - 1);
  const [top, setTop] = useState('dataMax+1');
  const [bottom, setBottom] = useState('dataMin-1');

  const zoom = () => {
    if (refAreaLeft === refAreaRight || refAreaRight === '') {
      setRefAreaLeft('');
      setRefAreaRight('');
      return;
    }

    // Ensure left is less than right
    if (refAreaLeft > refAreaRight) {
      const temp = refAreaLeft;
      setRefAreaLeft(refAreaRight);
      setRefAreaRight(temp);
    }

    setLeft(refAreaLeft);
    setRight(refAreaRight);
    setRefAreaLeft('');
    setRefAreaRight('');
  };

  const zoomOut = () => {
    setLeft(0);
    setRight(chartData.length - 1);
    setTop('dataMax+1');
    setBottom('dataMin-1');
  };

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Select 
              value={selectedRoute} 
              onChange={value => { setSelectedRoute(value); setSelectedTrip('all'); }}
              style={{ width: '100%' }}
              placeholder="Select route"
            >
              <Option value="all">All Routes</Option>
              {routes.map(route => (
                <Option key={route} value={route}>Route {route}</Option>
              ))}
            </Select>
          </Col>
          <Col span={12}>
            {selectedRoute !== 'all' && (() => {
              const tripIds = [...new Set(
                reportData.parsedData
                  .filter(item => item.routeNo === selectedRoute)
                  .map(item => item.tripId)
              )].sort();
              return (
                <Select
                  value={selectedTrip}
                  onChange={setSelectedTrip}
                  style={{ width: '100%' }}
                  placeholder="Select trip"
                >
                  <Option value="all">All Trips</Option>
                  {tripIds.map(tripId => {
                    // Find the earliest stop for this trip
                    const stopsForTrip = reportData.parsedData.filter(item =>
                      item.routeNo === selectedRoute &&
                      item.tripId === tripId
                    );
                    const earliest = stopsForTrip.reduce((min, stop) => {
                      if (!min) return stop;
                      return stop.scheduledTime < min.scheduledTime ? stop : min;
                    }, null);
                    let startTime = '';
                    if (earliest && earliest.scheduledTime) {
                      const timePart = earliest.scheduledTime.split(' ')[1];
                      if (timePart) {
                        const [h, m] = timePart.split(':');
                        startTime = `${h}:${m}`;
                      }
                    }
                    return (
                      <Option key={tripId} value={tripId}>
                        Trip {tripId} ({startTime})
                      </Option>
                    );
                  })}
                </Select>
              );
            })()}
          </Col>
        </Row>

        <Divider style={{ margin: '16px 0' }} />

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Space direction="horizontal" size="middle">
              <Space>
                <Switch 
                  checked={showStopTimes}
                  onChange={setShowStopTimes}
                />
                <Text>Show Actual Stop Times</Text>
              </Space>
              <Space>
                <Switch 
                  checked={showScheduledTime}
                  onChange={setShowScheduledTime}
                />
                <Text>Show Scheduled Time</Text>
              </Space>
              <Space>
                <Switch 
                  checked={showProposedTime}
                  onChange={setShowProposedTime}
                />
                <Text>Show Best OTR Time</Text>
              </Space>
              <Space>
                <Switch 
                  checked={showModelLines}
                  onChange={setShowModelLines}
                />
                <Text>Show Model Lines</Text>
              </Space>
            </Space>
          </Col>
        </Row>
      </div>
      {selectedRoute === 'all' || selectedTrip === 'all' ? (
        <Alert
          message="Select a Route and Trip"
          description={
            <Paragraph>
              Please select a specific route and trip from the filters above to view the service analysis.
              This will show you both the actual stop times and the proposed best OTR times.
            </Paragraph>
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      ) : !chartData || chartData.length === 0 ? (
        <Empty description="No data available for the selected route and trip" />
      ) : (
        <Card>
          {/* <div style={{ marginBottom: 16 }}>
            <Space>
              <Button onClick={zoomOut}>Reset Zoom</Button>
              <Text type="secondary">Click and drag on the chart to zoom</Text>
            </Space>
          </div> */}
          <div style={{ height: 800 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 50, bottom: 100 }}
                // onMouseDown={e => e && setRefAreaLeft(e.activeTooltipIndex)}
                // onMouseMove={e => refAreaLeft !== '' && e && setRefAreaRight(e.activeTooltipIndex)}
                // onMouseUp={zoom}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="stopName"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  domain={[left, right]}
                  allowDataOverflow={true}
                  tick={(props) => (
                    <CustomXAxisTick
                      {...props}
                      isTimingPoint={props.payload.isTimingPoint}
                    />
                  )}
                />
                <YAxis
                  tickFormatter={value => {
                    const hours = Math.floor(value / (60 * 60 * 1000));
                    const minutes = Math.floor((value % (60 * 60 * 1000)) / (60 * 1000));
                    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                  }}
                  domain={[bottom, top]}
                  scale="time"
                  type="number"
                  allowDataOverflow={true}
                />
                <RechartsTooltip
                  content={<CustomTooltip />}
                  labelFormatter={label => `Stop: ${label}`}
                  filterNull={true}
                />
                <Legend />
                {/* Scheduled Time Line */}
                {showScheduledTime && (
                  <Line
                    type="monotone"
                    dataKey="scheduledTime"
                    name="Scheduled Time"
                    stroke="#8884d8"
                    strokeWidth={3}
                    dot={true}
                  />
                )}
                {/* Proposed Time Line */}
                {showProposedTime && (
                  <Line
                    type="monotone"
                    dataKey="proposedTime"
                    name="Best OTR"
                    stroke="#52c41a"
                    strokeWidth={3}
                    dot={true}
                  />
                )}
                {/* Model Lines */}
                {showModelLines && (
                  <>
                    <Line
                      type="monotone"
                      dataKey="normalTime"
                      name="Normal Model"
                      stroke="#1890ff"
                      strokeDasharray="5 2"
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="empiricalTime"
                      name="Empirical Model"
                      stroke="#faad14"
                      strokeDasharray="5 2"
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="logNormalTime"
                      name="Log-Normal Model"
                      stroke="#d46b08"
                      strokeDasharray="5 2"
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="weibullTime"
                      name="Weibull Model"
                      stroke="#722ed1"
                      strokeDasharray="5 2"
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="robustTime"
                      name="Robust Model"
                      stroke="#13c2c2"
                      strokeDasharray="5 2"
                      dot={false}
                    />
                  </>
                )}
                {/* Actual Stop Time Lines */}
                {showStopTimes && tripDates.map((tripDate, idx) => (
                  <Line
                    key={`actual-${tripDate}`}
                    type="monotone"
                    dataKey={`actual_${tripDate}`}
                    name={`Actual (${tripDate})`}
                    stroke="#cccccc"
                    strokeWidth={1}
                    strokeOpacity={0.5}
                    dot={true}
                  />
                ))}

                {refAreaLeft !== '' && refAreaRight !== '' ? (
                  <ReferenceArea
                    x1={refAreaLeft}
                    x2={refAreaRight}
                    strokeOpacity={0.3}
                    fill="#8884d8"
                    fillOpacity={0.3}
                  />
                ) : null}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div style={{ marginTop: 16 }}>
            <Alert
              message="Service Analysis"
              description={
                <Paragraph>
                  This chart shows actual stop times (gray lines), the scheduled time (blue), and the proposed time (green) that would maximize on-time performance in the [-30, +120] second window.
                </Paragraph>
              }
              type="info"
              showIcon
            />
          </div>
        </Card>
      )}
    </>
  );
};

export default ServiceAnalysis; 